import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button"
import { ModeToggle } from "@/components/mode-toggle"
import Logo from "@/components/logo"
import LandingFooter from "@/components/LandingFooter"
import { useState, useEffect } from 'react';
import { Menu as MenuIcon, X } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

const LandingLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Add insecure authentication check
  useEffect(() => {
    const accessToken = localStorage.getItem('accesstoken');
    const user = localStorage.getItem('user');
    setIsAuthenticated(accessToken && user);
  }, []);

  // Add intersection observer to track sections
  useEffect(() => {
    const sections = ['features', 'pricing'];
    const observers = {};
    let visibleSections = new Set();

    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              visibleSections.add(sectionId);
            } else {
              visibleSections.delete(sectionId);
            }
            
            // If no sections are visible, clear the active section
            if (visibleSections.size === 0) {
              setActiveSection('');
            } else {
              // Get the first visible section
              const [firstVisible] = visibleSections;
              setActiveSection(firstVisible);
            }
          },
          {
            rootMargin: '-50% 0px -50% 0px'
          }
        );

        observer.observe(element);
        observers[sectionId] = observer;
      }
    });

    // Cleanup observers
    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);

  // Update active section when navigating directly to a section
  useEffect(() => {
    if (location.state?.scrollTo) {
      setActiveSection(location.state.scrollTo);
    } else if (location.pathname === '/') {
      setActiveSection('');
    }
  }, [location]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('');
    }
  };

  const handleAuthAction = () => {
    if (isAuthenticated) {
      navigate('/today');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="border-b fixed top-0 left-0 right-0 z-50 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="w-[200px]">
              <Logo onClick={handleLogoClick} />
            </div>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <MenuIcon className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  <Button 
                    className="w-full justify-start"
                    variant={activeSection === 'features' ? 'secondary' : 'ghost'} 
                    onClick={() => {
                      scrollToSection('features');
                      setIsOpen(false);
                    }}
                  >
                    Features
                  </Button>
                  <Button 
                    className="w-full justify-start"
                    variant={activeSection === 'pricing' ? 'secondary' : 'ghost'} 
                    onClick={() => {
                      scrollToSection('pricing');
                      setIsOpen(false);
                    }}
                  >
                    Pricing
                  </Button>
                  <Button 
                    className="w-full justify-start"
                    variant={location.pathname === '/changelog' ? 'secondary' : 'ghost'} 
                    onClick={() => {
                      navigate('/changelog');
                      setIsOpen(false);
                    }}
                  >
                    Changelog
                  </Button>
                  <Button 
                    className="w-full justify-start"
                    variant={location.pathname === '/support' ? 'secondary' : 'ghost'} 
                    onClick={() => {
                      navigate('/support');
                      setIsOpen(false);
                    }}
                  >
                    Support
                  </Button>
                  <Button 
                    className="w-full justify-start"
                    onClick={() => {
                      handleAuthAction();
                      setIsOpen(false);
                    }}
                  >
                    {isAuthenticated ? "Go to App" : "Login"}
                  </Button>
                  <div className="flex justify-start py-2">
                    <ModeToggle />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex-1 hidden md:flex justify-center">
              <div className="hidden md:flex space-x-4">
                <Button 
                  variant={activeSection === 'features' ? 'secondary' : 'ghost'} 
                  onClick={() => scrollToSection('features')}
                >
                  Features
                </Button>
                <Button 
                  variant={activeSection === 'pricing' ? 'secondary' : 'ghost'} 
                  onClick={() => scrollToSection('pricing')}
                >
                  Pricing
                </Button>
                <Button 
                  variant={location.pathname === '/changelog' ? 'secondary' : 'ghost'} 
                  onClick={() => navigate('/changelog')}
                >
                  Changelog
                </Button>
                <Button 
                  variant={location.pathname === '/support' ? 'secondary' : 'ghost'} 
                  onClick={() => navigate('/support')}
                >
                  Support
                </Button>
              </div>
            </div>
            <div className="hidden md:flex w-[200px] justify-end items-center space-x-4">
              <ModeToggle />
              <Button onClick={handleAuthAction}>
                {isAuthenticated ? "Go to App" : "Login"}
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="pt-16 flex-grow">
        {children}
      </main>
      <LandingFooter />
    </div>
  );
};

export default LandingLayout;
